import {
    Fragment,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useRef
} from 'react';
import classnames from 'classnames/bind';
import { isFunction } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useToggleState } from 'app/lib/hooks';
import { DropdownMenu } from 'app/components/core/ComposableMenu';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Button, ButtonVariant } from 'app/components/core/Button';
import { DataCell } from './DataCell';
import type { TableColumn } from './types';
import * as styles from './Table.css';

const cx = classnames.bind(styles);
const PADDING = 4;

export interface RowProps<TRow> {
    columns: TableColumn<TRow>[];
    row: TRow;
    rowKey: keyof TRow;
    selected: boolean;
    detailsKey?: keyof TRow;
    detailsRenderer?: (value: unknown) => ReactNode;
    menuRenderer?: (row: TRow) => ReactNode;
    stickyIfExpanded?: boolean;
    onSelectRow?: (value: unknown) => void;
    rowClassName?: (row: TRow) => string;
    width?: number;
}

export function Row<TRow>({
    columns,
    row,
    rowKey,
    selected,
    detailsKey,
    detailsRenderer,
    stickyIfExpanded = false,
    menuRenderer,
    onSelectRow,
    rowClassName,
    width
}: RowProps<TRow>) {
    const { t } = useTranslation();
    const rowRef = useRef<HTMLTableRowElement>(null);
    const expandedRowRef = useRef<HTMLTableRowElement>(null);
    const hasDetailsColumn = isFunction(detailsRenderer);
    const hasDetailsContent = detailsKey ? Boolean(row[detailsKey]) : hasDetailsColumn;
    const [isExpanded, toggle] = useToggleState(false);

    const calculatedClassname = isFunction(rowClassName) ? rowClassName(row) : null;
    const classes = cx(calculatedClassname, { sticky: stickyIfExpanded });

    const isSelectable = isFunction(onSelectRow);

    function select() {
        if (isSelectable) {
            onSelectRow(row[rowKey]);
        }
    }

    const handleClick = (event: MouseEvent) => {
        if ((event.target as HTMLElement).tagName !== 'A') {
            select();
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case 'Enter':
                select();
                break;
            case 'ArrowUp': {
                event.stopPropagation();
                const prev = event.currentTarget.previousElementSibling;
                if (prev?.hasAttribute('tabindex')) {
                    (prev as HTMLElement).click();
                }
                break;
            }
            case 'ArrowDown': {
                event.stopPropagation();
                const next = event.currentTarget.nextElementSibling;
                if (next?.hasAttribute('tabindex')) {
                    (next as HTMLElement).click();
                }
                break;
            }
            default:
        }
    };

    useEffect(() => {
        if (selected) {
            requestAnimationFrame(() => {
                rowRef.current?.focus();
            });
        }
    }, [selected]);

    useEffect(() => {
        if (selected) {
            requestAnimationFrame(() => {
                rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }, [selected]);

    return (
        <Fragment>
            <tr
                ref={rowRef}
                className={classes}
                aria-selected={selected}
                tabIndex={isSelectable ? 0 : -1}
                onKeyDown={isSelectable ? handleKeyDown : undefined}
                onClick={isSelectable ? handleClick : undefined}>
                {hasDetailsColumn && (
                    <td className={styles.chevron}>
                        {hasDetailsContent && (
                            <button
                                aria-controls={`details-${row[rowKey]}`}
                                aria-expanded={isExpanded}
                                onClick={toggle}>
                                <Icon
                                    variant={isExpanded ? 'chevron-down-big' : 'chevron-right-big'}
                                    size={IconSize.EXTRA_SMALL}
                                />
                            </button>
                        )}
                    </td>
                )}
                {columns.map((column) => (
                    <DataCell key={column.key ?? column.field} row={row} column={column} />
                ))}
                {isFunction(menuRenderer) && (
                    <td className={styles.menu}>
                        <DropdownMenu
                            trigger={
                                <Button
                                    variant={ButtonVariant.GHOST}
                                    icon="more"
                                    label={t('components:Table.more')}
                                />
                            }
                            placement="bottom-end"
                            arrow>
                            {menuRenderer(row)}
                        </DropdownMenu>
                    </td>
                )}
            </tr>
            {isExpanded && (
                <tr ref={expandedRowRef} id={`details-${row[rowKey]}`}>
                    <td
                        colSpan={100}
                        className={styles.detailsRow}
                        style={width ? { maxWidth: width - PADDING } : undefined}>
                        {isFunction(detailsRenderer)
                            ? detailsRenderer(detailsKey ? row[detailsKey] : row)
                            : null}
                    </td>
                </tr>
            )}
        </Fragment>
    );
}
